/**
 *
 * TimePicker
 *
 */

import {
  Box,
  Button,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React, { memo, useEffect, useRef, useState } from 'react';

import TimeIcon from 'assets/v2/TimeIcon';
import { COLORS, newLayout } from 'theme';

import {
  Heading,
  MeridiemContainer,
  Separator,
  StyledPopover,
  TimeContainer,
} from './TimePicker.styles';
import InputField from '../InputField';

const MERIDIEM = {
  AM: 'AM',
  PM: 'PM',
};

const isValidTime = (value, { from, to }) => {
  if (!/^\d+$/.test(value)) {
    return false;
  }

  const numericValue = Number(value);
  if (numericValue < from || numericValue > to) {
    return false;
  }

  return true;
};

const TimePicker = ({
  clearAction,
  defaultOpen,
  inputProps,
  pickerProps,
  onChange,
  value,
  required,
  label,
  error,
  valid,
  disabled,
  onBlur,
  EndAdornmentIcon, // Optional status icon (e.g. "this field changed"); takes the picker button's slot
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen ?? false);
  const [hours, setHours] = useState('');
  const [minutes, setMinutes] = useState('');
  const [meridiem, setMeridiem] = useState(MERIDIEM.AM);
  const inputRef = useRef();
  const hoursInputRef = useRef();

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const datetime = DateTime.fromFormat(
      `${hours}:${minutes} ${meridiem}`,
      'h:mm a',
      {
        suppressMilliseconds: true,
      },
    );
    if (datetime.isValid) {
      const formattedTime = datetime.toFormat('h:mm a');
      if (value !== formattedTime) {
        onChange(formattedTime);
      }
    }
  }, [hours, minutes, meridiem, value]);

  const clear = () => {
    setHours('');
    setMinutes('');
    setMeridiem(MERIDIEM.AM);
  };

  const handleClear = () => {
    clear();
    hoursInputRef.current.focus();
  };

  const handleHoursChange = (event) => {
    const { value: val } = event.target;

    if (!val) {
      setHours('');
      return;
    }

    if (isValidTime(val, { from: 1, to: 12 })) {
      setHours(String(val));
    }
  };

  const handleMinutesChange = (event) => {
    const { value: val } = event.target;

    if (!val) {
      setMinutes('');
      return;
    }

    if (isValidTime(val, { from: 0, to: 59 })) {
      setMinutes(String(+val).padStart(2, '0'));
    }
  };

  const handleMeridiemChange = (_event, val) => {
    if (!val) {
      return;
    }

    setMeridiem(val);
  };

  const handleSetTimeToStates = () => {
    if (!value) {
      return;
    }

    const datetime = DateTime.fromFormat(value, 'h:mm a', {
      suppressMilliseconds: true,
    });

    if (
      !datetime.isValid ||
      !isValidTime(value.split(':')[0], { from: 1, to: 12 })
    ) {
      return;
    }

    const [hourMinute, meridiemPart] = value.split(' ');
    const [hourPart, minutePart] = hourMinute.split(':');
    setHours(hourPart);
    setMinutes(minutePart);
    setMeridiem(meridiemPart.toUpperCase());
  };

  const handlePickerShow = () => {
    if (inputProps?.disabled) {
      return;
    }
    setIsOpen(true);
    handleSetTimeToStates();
  };

  return (
    <div ref={inputRef}>
      <InputField
        label={required ? `${label} *` : label}
        value={value}
        mask="time"
        onChange={onChange}
        valid={valid}
        error={error}
        InputProps={{
          ...inputProps,
          // Only one icon at a time — a status icon replaces the clock glyph, but it
          // swaps out the glyph *inside* the button rather than the button itself, so
          // the picker still opens on click. Errors outrank it.
          endAdornment: (
            <Button
              size="icon"
              variant="text"
              disabled={disabled}
              onClick={handlePickerShow}
              sx={{
                '&.MuiButtonBase-root': {
                  marginRight: `-${newLayout.spacing.s3}`,
                  display: 'inline-flex',
                  padding: newLayout.spacing.s2,
                  borderRadius: '50%',
                  fontSize: newLayout.fontSize.h3,

                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                },
              }}
            >
              {valid !== false && EndAdornmentIcon ? (
                EndAdornmentIcon
              ) : (
                <TimeIcon sx={{ color: COLORS.black50 }} />
              )}
            </Button>
          ),
        }}
        disabled={disabled}
        {...props}
      />
      <StyledPopover
        open={isOpen}
        anchorEl={inputRef.current}
        onClose={() => {
          setIsOpen(false);
          handleClear();
          onBlur?.();
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        {...pickerProps}
      >
        <Heading variant="h4" fontWeight="medium">
          Enter time
        </Heading>
        <TimeContainer>
          <div>
            <TextField
              value={hours}
              autoFocus
              inputRef={hoursInputRef}
              onChange={handleHoursChange}
            />
          </div>
          <Separator>:</Separator>
          <div>
            <TextField value={minutes} onChange={handleMinutesChange} />
          </div>
          <MeridiemContainer>
            <ToggleButtonGroup
              orientation="vertical"
              exclusive
              fullWidth
              aria-label="Meridiem"
              value={meridiem}
              onChange={handleMeridiemChange}
              sx={{
                '&&& .MuiToggleButton-root': {
                  display: 'flex',
                  justifyContent: 'center',
                  whiteSpace: 'nowrap',
                },
              }}
            >
              <ToggleButton value="AM" aria-label="AM">
                AM
              </ToggleButton>
              <ToggleButton value="PM" aria-label="PM">
                PM
              </ToggleButton>
            </ToggleButtonGroup>
          </MeridiemContainer>
        </TimeContainer>
        {clearAction && (
          <Box
            display="grid"
            gridAutoFlow="column"
            gridTemplateColumns="1fr"
            gap="8px"
            mt="8px"
          >
            <Button
              size="small"
              disabled={!value}
              onClick={handleClear}
              sx={{ marginLeft: 'auto' }}
            >
              Clear
            </Button>
          </Box>
        )}
      </StyledPopover>
    </div>
  );
};

TimePicker.propTypes = {
  clearAction: PropTypes.bool,
  defaultOpen: PropTypes.bool,
  inputProps: PropTypes.object,
  pickerProps: PropTypes.object,
  onChange: PropTypes.func,
  value: PropTypes.string,
  required: PropTypes.bool,
  label: PropTypes.string,
  error: PropTypes.string,
  valid: PropTypes.bool,
  disabled: PropTypes.bool,
  onBlur: PropTypes.func,
  EndAdornmentIcon: PropTypes.element,
};

export default memo(TimePicker);
